import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AgingProduct } from '../hooks/useOverviewData';
import { downloadCsv, todayYmd } from '../utils/csv';
import ExportButton from './ExportButton';

interface Props {
    products: AgingProduct[] | null;
    loading: boolean;
}

const DAY_SECONDS = 86_400;

// Severity bands (in days) — calibrated to feel right on a manufacturing
// workflow where most stages turn over within 1-3 days. Tweak per-tenant if
// needed; rendered as colored pills next to the duration.
const SEVERITY = {
    low: { threshold: 1, label: 'analyticsV2.aging.severity.fresh', tone: 'low' as const },
    medium: { threshold: 3, label: 'analyticsV2.aging.severity.attention', tone: 'medium' as const },
    high: { threshold: 7, label: 'analyticsV2.aging.severity.urgent', tone: 'high' as const },
    critical: { threshold: Infinity, label: 'analyticsV2.aging.severity.critical', tone: 'critical' as const },
};

type Severity = 'low' | 'medium' | 'high' | 'critical';

const classifySeverity = (stuckSeconds: number): Severity => {
    const days = stuckSeconds / DAY_SECONDS;
    if (days < SEVERITY.low.threshold) return 'low';
    if (days < SEVERITY.medium.threshold) return 'medium';
    if (days < SEVERITY.high.threshold) return 'high';
    return 'critical';
};

const AgingBoardCard: React.FC<Props> = ({ products, loading }) => {
    const intl = useIntl();
    const locale = intl.locale;

    const rows = useMemo(() => {
        return (products ?? []).map((p) => ({
            ...p,
            severity: classifySeverity(p.stuck_seconds),
            stuckLabel: formatStuckDuration(p.stuck_seconds, intl),
        }));
    }, [products, intl]);

    const handleExport = () => {
        if (!products || products.length === 0) return;
        downloadCsv<AgingProduct & { rank: number; days: number }>(
            `aging-products-${todayYmd()}.csv`,
            [
                { header: 'Rank', accessor: (r) => r.rank },
                { header: 'Product', accessor: (r) => r.article_name },
                { header: 'Client', accessor: (r) => r.client_name },
                { header: 'Stage', accessor: (r) => r.responsibility_name },
                { header: 'Days stuck', accessor: (r) => r.days.toFixed(1) },
                { header: 'Started at', accessor: (r) => r.started_at ?? '' },
            ],
            products.map((p, i) => ({
                ...p,
                rank: i + 1,
                days: p.stuck_seconds / DAY_SECONDS,
            })),
        );
    };

    return (
        <section className="av2-card av2-aging">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage
                            id="analyticsV2.aging.title"
                            defaultMessage="Products needing attention"
                        />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.aging.subtitle"
                            defaultMessage="Active product stages ordered by how long they've been open"
                        />
                    </p>
                </div>
                <ExportButton
                    onClick={handleExport}
                    disabled={!products || products.length === 0}
                />
            </div>
            <div className="av2-card__body">
                {loading || !products ? (
                    <AgingSkeleton count={5} />
                ) : products.length === 0 ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.aging.empty"
                            defaultMessage="Nothing is stuck — every active stage is fresh."
                        />
                    </div>
                ) : (
                    <ol className="av2-aging__rows">
                        {rows.map((row, idx) => (
                            <li
                                key={row.status_id}
                                className={`av2-aging__row av2-aging__row--${row.severity}`}
                                style={{ animationDelay: `${idx * 40}ms` }}
                            >
                                <span className="av2-aging__rank">{idx + 1}</span>
                                <div className="av2-aging__label">
                                    <span className="av2-aging__product">{row.article_name || '—'}</span>
                                    <span className="av2-aging__client">
                                        {row.client_name || '—'}
                                        {row.responsibility_name && (
                                            <>
                                                <span className="av2-aging__sep">·</span>
                                                <span className="av2-aging__stage">
                                                    {row.responsibility_name}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                </div>
                                <div className="av2-aging__meta">
                                    <span className={`av2-aging__pill av2-aging__pill--${row.severity}`}>
                                        {row.stuckLabel}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </section>
    );
};

const AgingSkeleton: React.FC<{ count: number }> = ({ count }) => (
    <ol className="av2-aging__rows">
        {Array.from({ length: count }, (_, i) => (
            <li key={i} className="av2-aging__row av2-aging__row--skeleton">
                <div className="av2-skel av2-skel--rank" />
                <div className="av2-aging__label">
                    <div className="av2-skel av2-skel--aging-product" />
                    <div className="av2-skel av2-skel--aging-client" />
                </div>
                <div className="av2-skel av2-skel--aging-pill" />
            </li>
        ))}
    </ol>
);

const formatStuckDuration = (
    seconds: number,
    intl: { formatMessage: (msg: { id: string; defaultMessage?: string }, values?: Record<string, unknown>) => string },
): string => {
    const days = Math.floor(seconds / DAY_SECONDS);
    const hours = Math.floor((seconds % DAY_SECONDS) / 3600);
    if (days >= 1) {
        return intl.formatMessage(
            {
                id: 'analyticsV2.aging.stuckDays',
                defaultMessage: '{days, plural, one {# day} other {# days}}',
            },
            { days },
        );
    }
    if (hours >= 1) {
        return intl.formatMessage(
            {
                id: 'analyticsV2.aging.stuckHours',
                defaultMessage: '{hours, plural, one {# hour} other {# hours}}',
            },
            { hours },
        );
    }
    return intl.formatMessage({
        id: 'analyticsV2.aging.stuckJustStarted',
        defaultMessage: 'just started',
    });
};

export default AgingBoardCard;
